import type { Stripe, StripeConstructorOptions, StripeElements } from '@stripe/stripe-js'
import * as React from 'react'
import { BillingWorkspace } from '@alice-financial/alice-sdk'

const loadStripe = (publishableKey: string, options?: StripeConstructorOptions | undefined) =>
  import('@stripe/stripe-js').then((stripe) => stripe.loadStripe(publishableKey, options))

/**
 * Load the Stripe instance for billing-related operations.
 */
export const useBillingStripe = (billingWorkspace?: BillingWorkspace) => {
  const stripeApiKey =
    billingWorkspace === BillingWorkspace.Pretaxhero
      ? import.meta.env.PUBLIC_STRIPE_PTH_BILLING_PUBLIC_KEY
      : import.meta.env.PUBLIC_STRIPE_BILLING_PUBLIC_KEY
  return React.useMemo(() => loadStripe(stripeApiKey || ''), [stripeApiKey])
}

/**
 * Load the Stripe instance for issuing-related operations.
 */
export const useIssuingStripe = ({ stripeAccount }: { stripeAccount: string | null | undefined }) =>
  React.useMemo(
    () =>
      stripeAccount
        ? loadStripe(import.meta.env.PUBLIC_STRIPE_ISSUING_PUBLIC_KEY || '', { stripeAccount })
        : null,
    [stripeAccount]
  )

export type IssuingElements = {
  stripe: Stripe
  elements: StripeElements
}

export const useIssuingElementsStripe = ({
  stripeAccount,
}: {
  stripeAccount: string
}): Promise<IssuingElements> =>
  React.useMemo(
    () =>
      loadStripe(import.meta.env.PUBLIC_STRIPE_ISSUING_PUBLIC_KEY || '', { stripeAccount }).then(
        (maybeStripe) => {
          if (!maybeStripe) throw new Error('Failed to load Stripe')
          return { stripe: maybeStripe, elements: maybeStripe.elements() }
        }
      ),
    [stripeAccount]
  )

// stripe.js beta features required for WPP (web push provisioning) "add to apple card" button.
// For some reason we can't just include betas above and use a single useIssuingElementsStripe instance,
// this causes API failures when attempting to retrieve card data with the ephemeral token, unclear why.
const betas = ['issuing_add_to_wallet_button_element_1', 'issuing_elements_2']
export const useBetaIssuingElementsStripe = ({
  stripeAccount,
}: {
  stripeAccount: string | null | undefined
}): Promise<IssuingElements> => {
  return React.useMemo(
    () => {
      if (!stripeAccount) {
        return Promise.reject(new Error('Stripe account is required for beta issuing elements'))
      }
      return loadStripe(import.meta.env.PUBLIC_STRIPE_ISSUING_PUBLIC_KEY || '', { stripeAccount, betas }).then(
        (maybeStripe) => {
          if (!maybeStripe) throw new Error('Failed to load Stripe')
          return { stripe: maybeStripe, elements: maybeStripe.elements() }
        }
      )
    },
    [stripeAccount]
  )
}
